/* import __COLOCATED_TEMPLATE__ from './phone-dialler-modal.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { type NewConversationWireFormat } from 'embercom/lib/inbox2/types';
import PhoneCountryCodes, {
  type CountryCode,
  countryFromIncompleteNumber,
} from 'embercom/lib/phone-codes';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import type PhoneNumber from 'embercom/models/inbox/phone-number';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import TeamSummary from 'embercom/objects/inbox/team-summary';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import type InboxState from 'embercom/services/inbox-state';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type TwilioService from 'embercom/services/twilio-service';
import ajax, { get, post, put } from 'embercom/lib/ajax';
import type { PhoneReusableWorkflow } from 'embercom/objects/phone/reusable-workflow';

interface Args {
  phoneNumber?: string;
  countryCode?: string;
}

export default class PhoneDiallerModal extends Component<Args> {
  @service declare twilioService: TwilioService;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomCallService: IntercomCallService;
  @service declare inboxState: InboxState;
  @service declare router: RouterService;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare inboxApi: InboxApi;
  @service declare permissionsService: any;

  @tracked toPhoneNumber = this.args.phoneNumber || '';
  @tracked phoneNumbers: any[] = [];
  @tracked phoneNumberGroupList?: any;
  @tracked selectedPhoneNumber?: string;
  @tracked selectedCountry?: CountryCode;
  @tracked countryCode = '';
  @tracked selectedMode = 'keypad';
  @tracked isActivelyCalling = false;
  @tracked isActivelyCallingId?: string | number;
  @tracked listOfTransferOptions: any;
  @tracked phoneWorkflows: PhoneReusableWorkflow[] = [];
  @tracked workflowsLoadFailed = false;
  @tracked defaultOutboundPhoneNumberId?: string;
  @tracked isSavingDefault = false;
  @tracked favoriteTransferTargets: Array<{ type: string; id: string | number }> = [];

  phoneNumbersMap: { [key: string]: string } = {};

  get isOutboundMode() {
    return this.intercomCallService.diallerContext === 'outbound';
  }

  get isConferenceMode() {
    return this.intercomCallService.diallerContext === 'conference';
  }

  get isCallTransferMode() {
    return this.intercomCallService.diallerContext === 'transfer';
  }

  get isWarmTransferMode() {
    return this.intercomCallService.diallerContext === 'warm-transfer';
  }

  get isTransferFavoritesEnabled() {
    return this.session.workspace.isFeatureEnabled('phone-transfer-favorites');
  }

  get showFavoritesSection() {
    return (
      this.isTransferFavoritesEnabled &&
      (this.isCallTransferMode || this.isWarmTransferMode || this.isConferenceMode)
    );
  }

  get callButtonLabel() {
    switch (this.intercomCallService.diallerContext) {
      case 'conference':
        return 'calling.dialler-modal.add-to-call-button-label';
      case 'transfer':
        return 'calling.dialler-modal.transfer-call-button-label';
      case 'warm-transfer':
        return 'calling.dialler-modal.warm-transfer-call-button-label';
      default:
        return 'calling.dialler-modal.call-button-label';
    }
  }

  get callButtonIcon() {
    switch (this.intercomCallService.diallerContext) {
      case 'conference':
        return 'add-participant';
      case 'transfer':
      case 'warm-transfer':
        return 'phone-transfer';
      default:
        return 'phone';
    }
  }

  get callButtonProcessingLabel() {
    switch (this.intercomCallService.diallerContext) {
      case 'conference':
        return 'calling.dialler-modal.adding-to-call-button-label';
      case 'transfer':
        return 'calling.dialler-modal.transfer-call-button-processing-label';
      case 'warm-transfer':
        return 'calling.dialler-modal.warm-transfer-call-button-processing-label';
      default:
        return 'calling.dialler-modal.calling-button-label';
    }
  }

  get isOutboundColdTransferEnabled() {
    return (
      this.session.workspace.isFeatureEnabled('phone-outbound-cold-transfer') &&
      this.twilioService.isActiveCall
    );
  }

  get showAdminAndTeamsListsForTransfer() {
    if (this.isCallTransferMode) {
      if (!this.twilioService.incomingCall && !this.isOutboundColdTransferEnabled) {
        return false;
      }
    }

    return true;
  }

  get searchPlaceholder() {
    if (this.showTeamsList && this.showAdminList) {
      return 'calling.dialler-modal.user-team-teammate-search-placeholder';
    } else if (this.showAdminList) {
      return 'calling.dialler-modal.teammate-user-search-placeholder';
    } else {
      return 'calling.dialler-modal.user-search-placeholder';
    }
  }

  get showAdminList() {
    if (this.isOutboundMode) {
      return false;
    }

    if (!this.showAdminAndTeamsListsForTransfer) {
      return false;
    }

    return true;
  }

  get showTeamsList() {
    if (!this.isCallTransferMode && !this.isWarmTransferMode) {
      return false;
    }

    return this.showAdminAndTeamsListsForTransfer;
  }

  get isAllowWorkflowTransfer() {
    return this.isCallTransferMode;
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (!this.isOutboundMode) {
      this.selectedMode = 'user_search';
    }

    this.phoneNumberChanged();

    if (this.args.countryCode) {
      this.setPhoneNumberPrefix(this.args.countryCode);
    }

    this.getWorkspacePhoneNumbers();

    if (this.isAllowWorkflowTransfer) {
      taskFor(this.fetchPhoneWorkflows).perform();
    }
  }

  async getWorkspacePhoneNumbers() {
    if (!this.intercomCallService.isWorkspacePhoneNumbersLoaded) {
      await this.intercomCallService.getWorkspacePhoneNumbers();
    }

    await this.loadDefaultOutboundPhoneNumber();
    let defaultPhoneNumber = this.intercomCallService.workspacePhoneNumbers.find(
      (p: PhoneNumber) => p.id === this.defaultOutboundPhoneNumberId,
    );
    this.selectedPhoneNumber =
      defaultPhoneNumber?.phoneNumber ||
      this.intercomCallService.workspacePhoneNumbers[0]?.phoneNumber;
    this.buildPhoneNumberGroupList();
    if (
      !this.toPhoneNumber &&
      !this.selectedCountry &&
      this.intercomCallService.workspacePhoneNumbers.length > 0
    ) {
      this.selectedCountry = this.intercomCallService.workspacePhoneNumbers[0].countryIsoCode;
      this.setPhoneNumberPrefix(this.selectedCountry);
    }
    this.phoneNumbers.forEach((phoneNumber) => {
      this.phoneNumbersMap[phoneNumber.value] = phoneNumber.text;
    });
  }

  private buildPhoneNumberGroupList() {
    this.phoneNumbers = this.intercomCallService.workspacePhoneNumbers.map(
      (phoneNumber: PhoneNumber) => ({
        text: phoneNumber.name,
        value: phoneNumber.phoneNumber,
        isSelected: phoneNumber.phoneNumber === this.selectedPhoneNumber,
        component: 'inbox2/phone-number-option',
        componentAttrs: {
          name: phoneNumber.name,
          phoneNumber: phoneNumber.phoneNumber,
          isDefault: phoneNumber.id === this.defaultOutboundPhoneNumberId,
          onToggleDefault: () => this.toggleDefaultForPhoneNumber(phoneNumber),
        },
      }),
    );
    this.phoneNumberGroupList = [{ items: this.phoneNumbers, hasItemsMarkableAsSelected: true }];
  }

  private async loadDefaultOutboundPhoneNumber() {
    try {
      let teammateId = this.session.teammate.id;
      let response = await get(`/ember/admins/teammate_app_settings/${teammateId}`, {
        app_id: this.session.workspace.id,
      });
      this.defaultOutboundPhoneNumberId = response.default_outbound_phone_number_id?.toString();
      this.favoriteTransferTargets = response.favorite_transfer_targets ?? [];
    } catch {
      // Silently fail — star just won't be filled
    }
  }

  get countries() {
    return Object.values(PhoneCountryCodes).map((phoneCodeObject: any) => ({
      text: this.intl.t(phoneCodeObject.nameTranslationKey),
      value: phoneCodeObject.code,
      component: 'inbox2/country-code-option',
      componentAttrs: {
        name: this.intl.t(phoneCodeObject.nameTranslationKey),
        code: phoneCodeObject.dial_code,
        flagUrl: phoneCodeObject.asset_url,
      },
    }));
  }

  @action
  setPhoneNumberPrefix(country: CountryCode) {
    // The country should always be in uppercase. However, `PhoneCountryCodes` used to use keys in lowercase,
    // and some customers may still have a lowercase value in their localStorage ['last-dialled-country-code']
    this.selectedCountry = country.toUpperCase();
    this.toPhoneNumber = this.toPhoneNumber.replace(this.countryCode, '').replace(' ', '');
    this.countryCode = PhoneCountryCodes[this.selectedCountry].dial_code;
    this.toPhoneNumber = `${this.countryCode} ${this.toPhoneNumber}`;
  }

  get selectedCountryFlagAssetUrl() {
    let countryCode = 'US';

    if (this.selectedCountry) {
      countryCode = this.selectedCountry;
    } else if (this.intercomCallService.defaultCountryCode) {
      countryCode = this.intercomCallService.defaultCountryCode;
    }

    return PhoneCountryCodes[countryCode].asset_url;
  }

  async createConversation(contactId: string) {
    let user = await this.inboxSearchSuggestionsService.loadUserFromId(contactId);
    if (!user) {
      this.notificationsService.notifyError(
        this.intl.t('calling.dialler-modal.find-or-create-contact-error'),
      );
      return;
    }

    if (!this.session.isTeammateLoaded) {
      await this.session.getTeammate(this.session.workspace.id);
    }

    let data: NewConversationWireFormat = {
      app_id: this.session.workspace.id,
      admin_assignee_id: this.session.teammate.id,
      team_assignee_id: TeamSummary.unassigned.id,
      sender_id: this.session.teammate.id,
      blocks: [],
      type: objectTypes.call,
      user_ids: [user.id],
    };

    return await this.inboxState.createNewConversation(data, [], false);
  }

  setCallingState(id?: string | number) {
    this.isActivelyCalling = true;
    this.isActivelyCallingId = id;
  }

  resetCallingState() {
    this.intercomCallService.nextCallIsTestCall = false;
    this.isActivelyCalling = false;
    this.isActivelyCallingId = undefined;
  }

  @action
  async callUser(user: UserSummary) {
    this.setCallingState(user.id);

    let useServerSideResolution =
      this.isOutboundMode && this.session.workspace.isFeatureEnabled('pii-attribute-masking');

    if (!useServerSideResolution) {
      let phoneNumber = user.phone;
      if (!phoneNumber) {
        this.notificationsService.notifyError(
          this.intl.t('calling.dialler-modal.validate-phone-number-error'),
        );
        this.resetCallingState();
        return;
      }
      await this.callNumber(phoneNumber);
      this.resetCallingState();
      return;
    }

    // Stored-user call: let backend resolve the phone number (PII masking support)
    let conversation = await this.createConversation(user.id.toString());
    if (!conversation) {
      this.notificationsService.notifyError(
        this.intl.t('calling.dialler-modal.create-conversation-error'),
      );
      this.resetCallingState();
      return;
    }

    this.router.transitionTo('inbox.workspace.inbox.conversation.conversation', conversation.id);
    let conversationObj = await this.inboxApi.fetchConversation(conversation.id);
    await this.twilioService.callStoredUser(conversationObj, user, this.selectedPhoneNumber);
    this.intercomCallService.closeDialler();
    this.intercomCallService.registerCallEvent({
      action: 'called',
      object: 'dialler_modal',
      place: 'user_search',
    });

    this.resetCallingState();
  }

  @action
  async dialNumber() {
    this.setCallingState();

    let validatedPhoneNumber = await this.intercomCallService.getValidatedPhoneNumber(
      this.toPhoneNumber,
      this.selectedCountry!,
    );
    if (!validatedPhoneNumber) {
      this.notificationsService.notifyError(
        this.intl.t('calling.dialler-modal.validate-phone-number-error'),
      );
      this.resetCallingState();
      return;
    }

    this.intercomCallService.saveLastDialledCountry(this.selectedCountry);
    await this.callNumber(validatedPhoneNumber);
    this.resetCallingState();
  }

  async callNumber(phoneNumber: string) {
    if (!this.isOutboundMode) {
      await this.handleExternalCall(phoneNumber);
      return;
    }

    let contact = await this.intercomCallService.findOrCreateContact(phoneNumber);
    let conversation = await this.createConversation(contact.id);
    if (!conversation) {
      this.notificationsService.notifyError(
        this.intl.t('calling.dialler-modal.create-conversation-error'),
      );
      return;
    }

    this.router.transitionTo('inbox.workspace.inbox.conversation.conversation', conversation.id);
    let conversationObj = await this.inboxApi.fetchConversation(conversation.id);
    await this.twilioService.callNumber(
      phoneNumber,
      conversationObj,
      contact,
      this.selectedPhoneNumber,
    );
    this.intercomCallService.closeDialler();
    let callPlacedInMode = this.selectedMode === 'keypad' ? 'calling' : 'user_search';
    this.intercomCallService.registerCallEvent({
      action: 'called',
      object: 'dialler_modal',
      place: callPlacedInMode,
    });
  }

  @action
  close() {
    this.intercomCallService.closeDialler();
  }

  get selectedPhoneNumberText() {
    if (!this.selectedPhoneNumber) {
      return '';
    }

    return this.phoneNumbersMap[this.selectedPhoneNumber] || this.selectedPhoneNumber;
  }

  @action
  inputDigit(keyCode: string) {
    if (keyCode === '+') {
      this.toPhoneNumber = this.toPhoneNumber.slice(0, -2);
    }
    this.toPhoneNumber += keyCode;
    this.phoneNumberChanged();
  }

  @action
  phoneNumberChanged(event?: Event) {
    let phoneNumber = (event?.target as HTMLInputElement)?.value || this.toPhoneNumber;
    let country = countryFromIncompleteNumber(phoneNumber);
    if (country) {
      this.selectedCountry = country.code;
      this.countryCode = country.dial_code;
    }
  }

  @action
  setSelectedPhoneNumber(value: string) {
    if (!this.toPhoneNumber || this.toPhoneNumber.trim() === this.countryCode) {
      this.selectedCountry = this.intercomCallService.workspacePhoneNumbers.findBy(
        'phoneNumber',
        value,
      )?.countryIsoCode;
      this.setPhoneNumberPrefix(this.selectedCountry!);
    }
    this.selectedPhoneNumber = value;
    this.buildPhoneNumberGroupList();
  }

  async toggleDefaultForPhoneNumber(phoneNumberObj: PhoneNumber) {
    if (this.isSavingDefault) {
      return;
    }

    this.isSavingDefault = true;

    try {
      let isCurrentlyDefault = phoneNumberObj.id === this.defaultOutboundPhoneNumberId;
      let newDefaultId = isCurrentlyDefault ? null : phoneNumberObj.id;

      let teammateId = this.session.teammate.id;
      await put(`/ember/admins/teammate_app_settings/${teammateId}`, {
        app_id: this.session.workspace.id,
        default_outbound_phone_number_id: newDefaultId,
      });

      this.defaultOutboundPhoneNumberId = newDefaultId?.toString();
      this.buildPhoneNumberGroupList();

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          isCurrentlyDefault
            ? 'calling.dialler-modal.default-phone-number-cleared'
            : 'calling.dialler-modal.default-phone-number-saved',
        ),
      );

      this.intercomCallService.registerCallEvent({
        action: isCurrentlyDefault ? 'cleared' : 'set',
        object: 'default_outbound_phone_number',
        place: 'dialler_modal',
      });
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('calling.dialler-modal.default-phone-number-save-error'),
      );
    } finally {
      this.isSavingDefault = false;
    }
  }

  private async mutateTransferFavorite(
    action: 'add' | 'remove',
    type: string,
    id: string | number,
  ) {
    let previous = [...this.favoriteTransferTargets];
    let matches = (t: { type: string; id: string | number }) =>
      t.type === type && String(t.id) === String(id);

    if (action === 'add') {
      if (!previous.some(matches)) {
        this.favoriteTransferTargets = [...previous, { type, id }];
      }
    } else {
      this.favoriteTransferTargets = previous.filter((t) => !matches(t));
    }

    try {
      let response = await post(`/ember/admins/teammate_app_settings/${action}_transfer_favorite`, {
        app_id: this.session.workspace.id,
        type,
        id,
      });
      this.favoriteTransferTargets = response.favorite_transfer_targets ?? [];
    } catch (e: unknown) {
      this.favoriteTransferTargets = previous;
      this.handleTransferFavoriteError(e);
    }
  }

  private handleTransferFavoriteError(e: unknown) {
    if (typeof e === 'object' && e !== null && 'jqXHR' in e) {
      let { jqXHR } = e as {
        jqXHR: { status?: number; responseJSON?: { required_permission?: string } };
      };
      if (jqXHR?.status === 403 && jqXHR.responseJSON?.required_permission) {
        this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
          jqXHR.responseJSON.required_permission,
        );
        return;
      }
    }
    this.notificationsService.notifyError(
      this.intl.t('calling.dialler-modal.transfer-favorite-save-error'),
    );
  }

  @action
  toggleTransferFavorite(type: string, id: string | number) {
    let normalizedId = type === 'user' ? String(id) : Number(id);
    let isFavorited = this.favoriteTransferTargets.some(
      (t) => t.type === type && String(t.id) === String(normalizedId),
    );
    this.mutateTransferFavorite(isFavorited ? 'remove' : 'add', type, normalizedId);
  }

  @action switchMode(mode: string) {
    this.selectedMode = mode;
    this.intercomCallService.registerCallEvent({
      action: 'ui_mode_switched',
      object: 'dialler_modal',
      place: 'dialler_modal',
      context: mode,
    });
  }

  @action async handleCallToAdmin(admin: AdminSummary) {
    this.setCallingState(admin.id);

    if (this.isConferenceMode) {
      await taskFor(this.twilioService.addTeammateToCall).perform(admin);
    } else if (this.isCallTransferMode) {
      taskFor(this.twilioService.transferToAdmin).perform(admin);
    } else if (this.isWarmTransferMode) {
      taskFor(this.twilioService.warmTransferToAdmin).perform(admin);
    }

    this.close();
    this.resetCallingState();
  }

  @action handleCallToTeam(team: TeamSummary) {
    this.setCallingState(team.id);

    if (this.isCallTransferMode) {
      taskFor(this.twilioService.transferToTeam).perform(team.id);
    } else if (this.isWarmTransferMode) {
      taskFor(this.twilioService.warmTransferToTeam).perform(team);
    }

    this.close();
    this.resetCallingState();
  }

  @action async handleExternalCall(phoneNumber: string) {
    if (this.isConferenceMode) {
      await taskFor(this.twilioService.addExternalParticipantToCall).perform(phoneNumber);
    } else if (this.isCallTransferMode) {
      taskFor(this.twilioService.transferToExternalNumber).perform(phoneNumber);
    } else if (this.isWarmTransferMode) {
      await taskFor(this.twilioService.warmTransferToExternalNumber).perform(phoneNumber);
    }
    this.close();
  }

  @task({ drop: true })
  *fetchPhoneWorkflows(): TaskGenerator<void> {
    try {
      let workflows: PhoneReusableWorkflow[] = yield ajax({
        url: `/ember/operator_custom_bots/phone_partial_bots`,
        type: 'GET',
        data: {
          app_id: this.session.workspace.id,
          live_only: true,
          sort_by_admin_order: true,
        },
      });

      this.phoneWorkflows = workflows;
      this.workflowsLoadFailed = false;
    } catch (e) {
      this.phoneWorkflows = [];
      this.workflowsLoadFailed = true;
    }
  }

  @action handleCallToWorkflow(workflow: PhoneReusableWorkflow) {
    if (!workflow.id) {
      this.notificationsService.notifyError(
        this.intl.t('calling.dialler-modal.invalid-workflow-error'),
      );
      this.resetCallingState();
      return;
    }

    this.setCallingState(workflow.id);

    if (this.isCallTransferMode) {
      taskFor(this.twilioService.transferToWorkflow).perform(workflow.id, workflow.title);

      this.intercomCallService.registerCallEvent({
        action: 'clicked',
        object: 'workflow_transfer_button',
        section: 'dialler_modal',
        workflow_id: workflow.id,
      });
    }

    this.close();
    this.resetCallingState();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::PhoneDiallerModal': typeof PhoneDiallerModal;
    'inbox2/phone-dialler-modal': typeof PhoneDiallerModal;
  }
}
